import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { List, Box, ListItemText, Collapse, IconButton } from '@mui/material';
import { ExitToApp as ExitToAppIcon, ChevronLeft, ChevronRight, SupervisorAccount as SupervisorAccountIcon } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { selectUserPermissions } from '../../modules/user/selectors';

import OrgSidebarLogo from '../../assets/main/sidebar-logo.png'
import DashboardLogo from '../../assets/sidebar/dashboard-icon.svg'
import ManagementLogo from '../../assets/sidebar/management-icon.svg'
import ShiftLogo from '../../assets/sidebar/shift-monitoring.svg'
import ReportLogo from '../../assets/sidebar/report-icon.svg'
import SettingsLogo from '../../assets/sidebar/settings.svg'
import InvoiceListingLogo from '../../assets/sidebar/invoice-listing.svg'
import ShiftBoardLogo from '../../assets/sidebar/shift-board.svg'
import FacilityPendingLogo from '../../assets/sidebar/facility-pending-shift.svg'
import { useSidebar } from '../../context/SidebarContext';

import {
  LogoContainer,
  LogoText,
  OrgText,
  StyledDrawer,
  StyledListItemButton,
  StyledListItemIcon,
  ChildItem
} from './styles';

interface OrgOrgSidebarProps {
  onLogout: () => void;
}

const OrgSidebar: React.FC<OrgOrgSidebarProps> = ({ onLogout }) => {
  const { activePage, isCollapsed, toggleSidebar, setIsCollapsed } = useSidebar();
  const [isManagementSubMenuOpen, setManagementSubMenuOpen] = useState(false);
  const [isSettingsSubMenuOpen, setSettingsSubMenuOpen] = useState(false);
  const [isAccessControlSubMenuOpen, setAccessControlSubMenuOpen] = useState(false);
  const permissions = useSelector(selectUserPermissions);

  const hasAccess = (moduleSlug: string) => {
    return permissions[moduleSlug]?.have_read || permissions[moduleSlug]?.have_admin;
  };

  const showDashboard = hasAccess('org-dashboard');
  const showFacilities = hasAccess('facility-management');
  const showWorkers = hasAccess('worker-management');
  const showShifts = hasAccess('shift-monitoring');
  const showShiftRequests = hasAccess('shift-requests');
  const showReports = hasAccess('reports');
  const showInvoices = hasAccess('invoices');
  const showMasterData = hasAccess('master-data');
  const showSettings = hasAccess('settings');
  const showAccessControl = hasAccess('access-control');

  const showManagementGroup = showFacilities || showWorkers;
  const showSettingsGroup = showSettings || showMasterData;

  const isManagementActive = activePage === 'worker' || activePage === 'facilities';
  const isSettingsActive = activePage === 'role' || activePage === 'profile' || activePage === 'cancellation-reasons' || activePage === 'facility-type' || activePage === 'qualification' || activePage === 'document-types' || activePage === 'designation' || activePage === 'role-doc-mapping' || activePage === 'facility-type-doc-mapping' || activePage === 'bank' || activePage === 'config' || activePage === 'bank-form' || activePage === 'custom-form';
  const isAccessControlActive = activePage === 'org-system-roles' || activePage === 'org-role-mapping' || activePage === 'staff-management';

  const handleManagementClick = () => {
    if (isCollapsed) {
      setIsCollapsed(false);
      setManagementSubMenuOpen(true);
    } else {
      if (isManagementSubMenuOpen) {
        setIsCollapsed(true);
      }
      setManagementSubMenuOpen(!isManagementSubMenuOpen);
    }
  };

  const handleSettingsClick = () => {
    if (isCollapsed) {
      setIsCollapsed(false);
      setSettingsSubMenuOpen(true);
    } else {
      if (isSettingsSubMenuOpen) {
        setIsCollapsed(true);
      }
      setSettingsSubMenuOpen(!isSettingsSubMenuOpen);
    }
  };

  const handleAccessControlClick = () => {
    if (isCollapsed) {
      setIsCollapsed(false);
      setAccessControlSubMenuOpen(true);
    } else {
      if (isAccessControlSubMenuOpen) {
        setIsCollapsed(true);
      }
      setAccessControlSubMenuOpen(!isAccessControlSubMenuOpen);
    }
  };

  React.useEffect(() => {
    if (!isManagementActive) {
      setManagementSubMenuOpen(false);
    }
  }, [isManagementActive]);

  React.useEffect(() => {
    if (!isAccessControlActive) {
      setAccessControlSubMenuOpen(false);
    }
  }, [isAccessControlActive]);

  return (
    <>
      {/* 1. FIXED LOGO CONTAINER */}
      <LogoContainer collapsed={isCollapsed}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: isCollapsed ? 'center' : 'flex-start' }}>
          <img src={OrgSidebarLogo} alt="Logo" style={{ height: '32px', width: '32px' }} />
          {!isCollapsed && (
            <Box sx={{ ml: 2, display: 'flex', flexDirection: 'column' }}>
              <LogoText>Talent Hub</LogoText>
              <OrgText variant="caption">Organization</OrgText>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            position: 'absolute',
            right: -12,
            left: 'auto',
            bottom: 'auto',
            top: 20,
            transform: 'none',
            zIndex: 1300,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <IconButton
            onClick={toggleSidebar}
            size="small"
            sx={{
              backgroundColor: '#fff',
              border: '1px solid #e0e0e0',
              width: 24,
              height: 24,
              '&:hover': { backgroundColor: '#f5f5f5' }
            }}
          >
            {isCollapsed ? <ChevronRight fontSize="small" /> : <ChevronLeft fontSize="small" />}
          </IconButton>
        </Box>
      </LogoContainer>

      {/* 2. NAVIGATION DRAWER */}
      <StyledDrawer variant="permanent" collapsed={isCollapsed}>
        <List>
          {/* Dashboard */}
          {showDashboard && (
            <Link to="/dashboard" style={{ textDecoration: 'none', color: 'inherit' }}>
              <StyledListItemButton selected={activePage === 'dashboard'}>
                <StyledListItemIcon>
                  <img src={DashboardLogo} alt="Dashboard" style={{ height: '16px', width: '16px' }} />
                </StyledListItemIcon>
                {!isCollapsed && <ListItemText primary="Dashboard" />}
              </StyledListItemButton>
            </Link>
          )}

          {/* Management Group */}
          {showManagementGroup && (
            <>
              <StyledListItemButton selected={isManagementActive} onClick={handleManagementClick}>
                <StyledListItemIcon>
                  <img src={ManagementLogo} alt="Management" style={{ height: '16px', width: '16px' }} />
                </StyledListItemIcon>
                {!isCollapsed && <ListItemText primary="Management" />}
              </StyledListItemButton>

              <Collapse in={!isCollapsed && (isManagementSubMenuOpen || isManagementActive)} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {showFacilities && (
                    <Link to="/facilities" style={{ textDecoration: 'none', color: 'inherit' }}>
                      <ChildItem selected={activePage === 'facilities'}>
                        <Box sx={{ width: '6px', height: '6px', bgcolor: 'text.primary', mr: 2 }} />
                        <ListItemText primary="Facilities" />
                      </ChildItem>
                    </Link>
                  )}

                  {showWorkers && (
                    <Link to="/workers" style={{ textDecoration: 'none', color: 'inherit' }}>
                      <ChildItem selected={activePage === 'worker'}>
                        <Box sx={{ width: '6px', height: '6px', bgcolor: 'text.primary', mr: 2 }} />
                        <ListItemText primary="Worker" />
                      </ChildItem>
                    </Link>
                  )}
                </List>
              </Collapse>
            </>
          )}

          {/* Shift Monitoring Group */}
          {showShifts && (
            <>
              <Link to="/shifts-board" style={{ textDecoration: 'none', color: 'inherit' }}>
                <StyledListItemButton selected={activePage === 'shift-board'}>
                  <StyledListItemIcon>
                    <img src={ShiftBoardLogo} alt="Shift Board" style={{ height: '16px', width: '16px' }} />
                  </StyledListItemIcon>
                  {!isCollapsed && <ListItemText primary="Shift Board" />}
                </StyledListItemButton>
              </Link>

              <Link to="/shifts" style={{ textDecoration: 'none', color: 'inherit' }}>
                <StyledListItemButton selected={activePage === 'shift'}>
                  <StyledListItemIcon>
                    <img src={ShiftLogo} alt="Shift Monitoring" style={{ height: '16px', width: '16px' }} />
                  </StyledListItemIcon>
                  {!isCollapsed && <ListItemText primary="Shift Monitoring" />}
                </StyledListItemButton>
              </Link>

              <Link to="/facility-pending-shifts" style={{ textDecoration: 'none', color: 'inherit' }}>
                <StyledListItemButton selected={activePage === 'facility_pending_shifts'}>
                  <StyledListItemIcon>
                    <img src={FacilityPendingLogo} alt="Facility Pending Shifts" style={{ height: '16px', width: '16px' }} />
                  </StyledListItemIcon>
                  {!isCollapsed && <ListItemText primary="Facility Pending Shifts" />}
                </StyledListItemButton>
              </Link>
            </>
          )}

          {/* Shift Requests */}
          {showShiftRequests && (
            <Link to="/shift-requests" style={{ textDecoration: 'none', color: 'inherit' }}>
              <StyledListItemButton selected={activePage === 'shift-requests'}>
                <StyledListItemIcon>
                  <img src={ShiftLogo} alt="Shift Requests" style={{ height: '16px', width: '16px' }} />
                </StyledListItemIcon>
                {!isCollapsed && <ListItemText primary="Shift Requests" />}
              </StyledListItemButton>
            </Link>
          )}

          {/* Reports */}
          {showReports && (
            <Link to="/org-report-analysis" style={{ textDecoration: 'none', color: 'inherit' }}>
              <StyledListItemButton selected={activePage === 'report'}>
                <StyledListItemIcon>
                  <img src={ReportLogo} alt="Report-analytics" style={{ height: '16px', width: '16px' }} />
                </StyledListItemIcon>
                {!isCollapsed && <ListItemText primary="Report & Analytics" />}
              </StyledListItemButton>
            </Link>
          )}

          {/* Invoices */}
          {showInvoices && (
            <Link to="/facility-invoices" style={{ textDecoration: 'none', color: 'inherit' }}>
              <StyledListItemButton selected={activePage === 'facility-invoices'}>
                <StyledListItemIcon>
                  <img src={InvoiceListingLogo} alt="Facility Invoices" style={{ height: '16px', width: '16px' }} />
                </StyledListItemIcon>
                {!isCollapsed && <ListItemText primary="Facility Invoices" />}
              </StyledListItemButton>
            </Link>
          )}

          {/* Access Control Group */}
          {showAccessControl && (
            <>
              <StyledListItemButton selected={isAccessControlActive} onClick={handleAccessControlClick}>
                <StyledListItemIcon>
                  <SupervisorAccountIcon sx={{ fontSize: '18px', color: '#666' }} />
                </StyledListItemIcon>
                {!isCollapsed && <ListItemText primary="Access Control" />}
              </StyledListItemButton>

              <Collapse in={!isCollapsed && (isAccessControlSubMenuOpen || isAccessControlActive)} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  <Link to="/access-control/roles" style={{ textDecoration: 'none', color: 'inherit' }}>
                    <ChildItem selected={activePage === 'org-system-roles'}>
                      <Box sx={{ width: '6px', height: '6px', bgcolor: 'text.primary', mr: 2 }} />
                      <ListItemText primary="System Roles" />
                    </ChildItem>
                  </Link>
                  <Link to="/access-control/mapping" style={{ textDecoration: 'none', color: 'inherit' }}>
                    <ChildItem selected={activePage === 'org-role-mapping'}>
                      <Box sx={{ width: '6px', height: '6px', bgcolor: 'text.primary', mr: 2 }} />
                      <ListItemText primary="Role Permissions" />
                    </ChildItem>
                  </Link>
                  <Link to="/access-control/staff" style={{ textDecoration: 'none', color: 'inherit' }}>
                    <ChildItem selected={activePage === 'staff-management'}>
                      <Box sx={{ width: '6px', height: '6px', bgcolor: 'text.primary', mr: 2 }} />
                      <ListItemText primary="Staff Management" />
                    </ChildItem>
                  </Link>
                </List>
              </Collapse>
            </>
          )}

          {/* Settings & Master Data Group */}
          {showSettingsGroup && (
            <>
              <StyledListItemButton selected={isSettingsActive} onClick={handleSettingsClick}>
                <StyledListItemIcon>
                  <img src={SettingsLogo} alt="Settings" style={{ height: '16px', width: '16px' }} />
                </StyledListItemIcon>
                {!isCollapsed && <ListItemText primary="Settings" />}
              </StyledListItemButton>

              <Collapse in={!isCollapsed && (isSettingsSubMenuOpen || isSettingsActive)} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {showSettings && (
                    <Link to="/profile" style={{ textDecoration: 'none', color: 'inherit' }}>
                      <ChildItem selected={activePage === 'profile'}>
                        <Box sx={{ width: '6px', height: '6px', bgcolor: 'text.primary', mr: 2 }} />
                        <ListItemText primary="Profile" />
                      </ChildItem>
                    </Link>
                  )}

                  {showMasterData && (
                    <>
                      <Link to="/master-data/roles" style={{ textDecoration: 'none', color: 'inherit' }}>
                        <ChildItem selected={activePage === 'role'}>
                          <Box sx={{ width: '6px', height: '6px', bgcolor: 'text.primary', mr: 2 }} />
                          <ListItemText primary="Role" />
                        </ChildItem>
                      </Link>

                      <Link to="/master-data/cancellation-reasons" style={{ textDecoration: 'none', color: 'inherit' }}>
                        <ChildItem selected={activePage === 'cancellation-reasons'}>
                          <Box sx={{ width: '6px', height: '6px', bgcolor: 'text.primary', mr: 2 }} />
                          <ListItemText primary="Cancellation Reasons" />
                        </ChildItem>
                      </Link>

                      <Link to="/master-data/facility-types" style={{ textDecoration: 'none', color: 'inherit' }}>
                        <ChildItem selected={activePage === 'facility-type'}>
                          <Box sx={{ width: '6px', height: '6px', bgcolor: 'text.primary', mr: 2 }} />
                          <ListItemText primary="Facility Types" />
                        </ChildItem>
                      </Link>

                      <Link to="/master-data/qualifications" style={{ textDecoration: 'none', color: 'inherit' }}>
                        <ChildItem selected={activePage === 'qualification'}>
                          <Box sx={{ width: '6px', height: '6px', bgcolor: 'text.primary', mr: 2 }} />
                          <ListItemText primary="Qualifications" />
                        </ChildItem>
                      </Link>

                      <Link to="/master-data/document-types" style={{ textDecoration: 'none', color: 'inherit' }}>
                        <ChildItem selected={activePage === 'document-types'}>
                          <Box sx={{ width: '6px', height: '6px', bgcolor: 'text.primary', mr: 2 }} />
                          <ListItemText primary="Document Types" />
                        </ChildItem>
                      </Link>

                      <Link to="/master-data/designations" style={{ textDecoration: 'none', color: 'inherit' }}>
                        <ChildItem selected={activePage === 'designation'}>
                          <Box sx={{ width: '6px', height: '6px', bgcolor: 'text.primary', mr: 2 }} />
                          <ListItemText primary="Designations" />
                        </ChildItem>
                      </Link>

                      <Link to="/master-data/role-doc-mapping" style={{ textDecoration: 'none', color: 'inherit' }}>
                        <ChildItem selected={activePage === 'role-doc-mapping'}>
                          <Box sx={{ width: '6px', height: '6px', bgcolor: 'text.primary', mr: 2 }} />
                          <ListItemText primary="Role Doc Mapping" />
                        </ChildItem>
                      </Link>

                      <Link to="/master-data/facility-type-doc-mapping" style={{ textDecoration: 'none', color: 'inherit' }}>
                        <ChildItem selected={activePage === 'facility-type-doc-mapping'}>
                          <Box sx={{ width: '6px', height: '6px', bgcolor: 'text.primary', mr: 2 }} />
                          <ListItemText primary="Facility Type Doc Mapping" />
                        </ChildItem>
                      </Link>

                      <Link to="/master-data/banks" style={{ textDecoration: 'none', color: 'inherit' }}>
                        <ChildItem selected={activePage === 'bank'}>
                          <Box sx={{ width: '6px', height: '6px', bgcolor: 'text.primary', mr: 2 }} />
                          <ListItemText primary="Banks" />
                        </ChildItem>
                      </Link>

                      <Link to="/master-data/config" style={{ textDecoration: 'none', color: 'inherit' }}>
                        <ChildItem selected={activePage === 'config'}>
                          <Box sx={{ width: '6px', height: '6px', bgcolor: 'text.primary', mr: 2 }} />
                          <ListItemText primary="Config" />
                        </ChildItem>
                      </Link>

                      <Link to="/master-data/bank-forms" style={{ textDecoration: 'none', color: 'inherit' }}>
                        <ChildItem selected={activePage === 'bank-form'}>
                          <Box sx={{ width: '6px', height: '6px', bgcolor: 'text.primary', mr: 2 }} />
                          <ListItemText primary="Bank Forms" />
                        </ChildItem>
                      </Link>

                      <Link to="/master-data/custom-forms" style={{ textDecoration: 'none', color: 'inherit' }}>
                        <ChildItem selected={activePage === 'custom-form'}>
                          <Box sx={{ width: '6px', height: '6px', bgcolor: 'text.primary', mr: 2 }} />
                          <ListItemText primary="Custom Forms" />
                        </ChildItem>
                      </Link>
                    </>
                  )}
                </List>
              </Collapse>
            </>
          )}
        </List>

        {/* Logout */}
        <List sx={{ mt: 'auto' }}>
          <StyledListItemButton onClick={onLogout}>
            <StyledListItemIcon> <ExitToAppIcon /> </StyledListItemIcon>
            {!isCollapsed && <ListItemText primary="Logout" />}
          </StyledListItemButton>
        </List>
      </StyledDrawer>
    </>
  );
};

export default OrgSidebar;